import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import SEOHead from "@/components/SEOHead";
import Partners from "@/components/Partners";




const SECRET_CODE = "croissance";

const accent = { fontFamily: "'Playfair Display', serif", fontStyle: "italic" } as const;
const mono = { fontFamily: "'JetBrains Mono', monospace" } as const;

const formSchema = z.object({
  name: z.string().trim().min(2, "Nom requis").max(100),
  email: z.string().trim().email("Courriel invalide").max(255),
  phone: z
    .string()
    .trim()
    .refine((v) => v.replace(/\D/g, "").length >= 10, "Téléphone invalide (10 chiffres min)"),
  company: z.string().trim().min(1, "Entreprise requise").max(200),
  code: z
    .string()
    .refine(
      (v) =>
        (v ?? "")
          .normalize("NFD")
          .replace(/\p{Diacritic}/gu, "")
          .replace(/\s+/g, "")
          .toLowerCase() === SECRET_CODE,
      "Le code secret est dans la vidéo ! :)"
    ),
});

const VideoGratuite = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    code: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Video embedded via LazyYouTubeEmbed below

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = formSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      parsed.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: parsed.data.name,
        email: parsed.data.email,
        phone: parsed.data.phone,
        company: parsed.data.company,
        source: "Video Gratuite QR",
      };

      const [leadRes, mailRes] = await Promise.allSettled([
        supabase.functions.invoke("notify-new-lead", { body: payload }),
        supabase.functions.invoke("notify-videogratuite-lead-email", {
          body: {
            name: parsed.data.name,
            email: parsed.data.email,
            phone: parsed.data.phone,
            company: parsed.data.company,
          },
        }),
      ]);

      if (leadRes.status === "rejected" || (leadRes.status === "fulfilled" && leadRes.value.error)) {
        throw leadRes.status === "rejected" ? leadRes.reason : leadRes.value.error;
      }
      if (mailRes.status === "rejected" || (mailRes.status === "fulfilled" && mailRes.value.error)) {
        console.warn("Notification courriel maxim échouée:", mailRes);
      }

      toast.success("C'est noté. On vous redirige.");
      setTimeout(() => navigate("/videogratuite/merci"), 800);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'envoi. Réessaie.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Votre vidéo gratuite"
        description="Une vraie vidéo pro, tournée dans nos studios. Zéro engagement."
        canonical="/videogratuite"
        noIndex
      />

      <header className="border-b border-border">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 h-16 flex items-center">
          <Logo variant="full" size="md" />
        </div>
      </header>

      <main className="max-w-[880px] mx-auto px-6 md:px-8 py-16 md:py-24">
        {/* 1. HERO */}
        <section className="text-center mb-16 md:mb-24">
          <p
            className="text-xs uppercase tracking-[0.25em] text-primary mb-6"
            style={mono}
          >
            Vidéo gratuite
          </p>
          <h1 className="text-5xl md:text-7xl leading-[1.05] tracking-tight mb-6">
            <span className="text-foreground">Votre vidéo </span>
            <span className="text-primary" style={accent}>vous attend.</span>
          </h1>
          <p className="text-lg md:text-xl text-foreground/90 max-w-[640px] mx-auto leading-relaxed">
            Un vrai reel pro, tourné dans nos studios, prêt à publier. Zéro engagement. Vous repartez avec, point.
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            Regardez la vidéo, trouvez le code, débloquez votre accès.
          </p>
        </section>

        {/* 2. VIDEO */}
        <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-border bg-secondary mb-16 md:mb-24 shadow-2xl">
          <iframe
            src="https://www.youtube-nocookie.com/embed/q-FBJH-U2bc?rel=0&modestbranding=1&playsinline=1&iv_load_policy=3"
            title="Votre vidéo gratuite"
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>


        {/* 3. CE QUE VOUS RECEVEZ */}
        <section className="border border-border rounded-xl p-8 md:p-12 bg-card mb-6 md:mb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-primary mb-5" style={mono}>
            Ce que vous recevez
          </p>
          <h2 className="text-3xl md:text-4xl mb-5 leading-tight">
            <span className="text-foreground">Un reel </span>
            <span className="text-primary" style={accent}>prêt à publier.</span>
          </h2>
          <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
            Un vrai reel pour vos réseaux sociaux (90 secondes ou moins), tourné par notre équipe dans nos studios, monté et prêt à publier. Vous vous déplacez, on s'occupe du reste.
          </p>
        </section>

        {/* 4. POURQUOI C'EST GRATUIT */}
        <section className="border border-border rounded-xl p-8 md:p-12 bg-card mb-16 md:mb-24">
          <p className="text-xs uppercase tracking-[0.25em] text-primary mb-5" style={mono}>
            La vraie raison
          </p>
          <h2 className="text-3xl md:text-4xl mb-5 leading-tight">
            <span className="text-foreground">Pourquoi c'est </span>
            <span className="text-primary" style={accent}>gratuit?</span>
          </h2>
          <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
            Parce qu'on aime mieux vous montrer que vous convaincre. N'importe qui peut dire qu'il fait de la bonne job. Nous, on préfère vous le prouver. Vous repartez avec une vraie vidéo, faite par notre équipe, sans engagement pis sans carte de crédit. Si vous aimez ce qu'on fait, on jasera de la suite. Sinon, vous aurez quand même une vidéo de qualité dans vos mains. C'est tout.
          </p>
        </section>

        {/* 5. FORMULAIRE */}
        <section className="mb-16 md:mb-24">
          <div className="mb-8 text-center">
            <p
              className="text-xs uppercase tracking-[0.25em] text-primary mb-3"
              style={mono}
            >
              Accède au contenu
            </p>
            <h2 className="text-3xl md:text-4xl">
              <span className="text-foreground">Entre </span>
              <span className="text-primary" style={accent}>vos infos</span>
            </h2>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-5 bg-card border border-border rounded-xl p-6 md:p-10"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name">Nom complet</Label>
                <Input id="name" value={values.name} onChange={handleChange("name")} className="mt-2 h-12 bg-background" />
                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="company">Entreprise</Label>
                <Input id="company" value={values.company} onChange={handleChange("company")} className="mt-2 h-12 bg-background" />
                {errors.company && <p className="text-xs text-destructive mt-1">{errors.company}</p>}
              </div>
              <div>
                <Label htmlFor="email">Courriel</Label>
                <Input id="email" type="email" value={values.email} onChange={handleChange("email")} className="mt-2 h-12 bg-background" />
                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input id="phone" type="tel" value={values.phone} onChange={handleChange("phone")} className="mt-2 h-12 bg-background" />
                {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
              </div>
            </div>

            <div className="pt-2">
              <Label htmlFor="code">Code secret</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">Vous trouverez le code dans la vidéo.</p>
              <Input
                id="code"
                value={values.code}
                onChange={handleChange("code")}
                placeholder="Entre le code"
                className="h-12 bg-background tracking-widest"
                style={mono}
              />
              {errors.code && <p className="text-xs text-destructive mt-1">{errors.code}</p>}
            </div>

            <Button
              type="submit"
              disabled={loading}
              variant="hero"
              size="lg"
              className="w-full"
            >
              {loading ? "Envoi..." : "Débloquer mon accès"}
            </Button>

            <p className="text-xs text-muted-foreground text-center pt-1">
              On vous recontacte pour planifier votre tournage. Pas de spam, promis.
            </p>
          </form>
        </section>
      </main>

      {/* 6. SOCIAL PROOF */}
      <Partners />

      <footer className="border-t border-border">
        <div
          className="max-w-[1280px] mx-auto px-6 md:px-8 py-8 text-center text-xs text-muted-foreground"
          style={mono}
        >
          © {new Date().getFullYear()} Agence 8020
        </div>
      </footer>
    </div>
  );
};

export default VideoGratuite;
